// Helper to parse simple "DD/MM/YYYY", "MM/YYYY", or just "YYYY"
const parseSimpleDate = (str) => {
    const parts = str.split('/').map(Number);
    if (parts.length === 3) {
        // DD/MM/YYYY
        return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
    }
    if (parts.length === 2) {
        // MM/YYYY
        return new Date(parts[1], parts[0] - 1, 1).getTime();
    }
    if (parts.length === 1) {
        // YYYY
        return new Date(parts[0], 0, 1).getTime();
    }
    return null;
};

// Helper to parse "FROM ... TO ..." or "BET ... AND ..." by calculating mid-point
const parseDateRange = (str) => {
    // Normalize tokens
    const cleaned = str
        .replace(/FROM\s+|\s+TO\s+|BET\s+|\s+AND\s+/gi, '|')
        .trim();
    // Split to get the two date parts
    const [ , startStr, endStr ] = cleaned.split('|');
    if (!startStr || !endStr) return null;
    const startTime = parseSimpleDate(startStr) || null;
    const endTime = parseSimpleDate(endStr) || null;
    if (startTime && endTime) return (startTime + endTime) / 2;
    return startTime || endTime; // fallback if one side is missing
};

// Helper for "BEF", "AFT", "ABT" + year
const parseRelativeDate = (prefix, str) => {
    // str might be something like "BEF 1948" or "AFT 1869"
    const yearPart = str.replace(prefix, '').trim();
    const baseTime = parseSimpleDate(yearPart);
    if (!baseTime) return null;

    // Example: BEF => take "year - 1", AFT => "year + 1", ABT => same year
    const dateObj = new Date(baseTime);
    const year = dateObj.getFullYear();

    if (prefix === 'BEF') dateObj.setFullYear(year - 1);
    if (prefix === 'AFT') dateObj.setFullYear(year + 1);
    // ABT - no change

    return dateObj.getTime();
};

// Main parser for "expectedGregorian"
const parseExpectedGregorian = (str) => {
    // Empty => sort to top
    if (!str) return Number.NEGATIVE_INFINITY;

    // Ranges: FROM ... TO ..., BET ... AND ...
    if (str.startsWith('FROM') || str.startsWith('BET')) {
        const rangeValue = parseDateRange(str);
        return rangeValue !== null ? rangeValue : Number.NEGATIVE_INFINITY;
    }

    // Relative: BEF, AFT, ABT
    if (str.startsWith('BEF')) return parseRelativeDate('BEF', str) ?? Number.NEGATIVE_INFINITY;
    if (str.startsWith('AFT')) return parseRelativeDate('AFT', str) ?? Number.NEGATIVE_INFINITY;
    if (str.startsWith('ABT')) return parseRelativeDate('ABT', str) ?? Number.NEGATIVE_INFINITY;

    // Otherwise assume "DD/MM/YYYY" / "MM/YYYY" / "YYYY"
    const simpleValue = parseSimpleDate(str);
    return simpleValue !== null ? simpleValue : Number.NEGATIVE_INFINITY;
};

// Sort function - returns a new array sorted by date ascending
export const sortPeopleByGregorianDate = (people) => {
    return [...people].sort((a, b) => {
        const aVal = parseExpectedGregorian(a.birth?.date);
        const bVal = parseExpectedGregorian(b.birth?.date);
        return aVal - bVal;
    });
};

export const getCleanFileName = (filePath) => {
    const fullFileName = decodeURIComponent(filePath.split('/').pop());
    const match = fullFileName.match(/[^_]+_[^_]+_(.+)$/);
    return match ? match[1] : fullFileName;
};

export const getFileType = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    return {
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    }[extension] || 'application/octet-stream';
};
